import {
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    type ReactNode,
    type Ref,
} from "react";
// eslint-disable-next-line no-restricted-imports
import { Combobox as ComboboxBase } from "@base-ui/react/combobox";
import classNames from "classnames";
import { Loader } from "@design-system/Loader/Loader";
import { mergeRefs } from "@design-system/overlay/mergeRefs";
import { usePickerAnnouncement } from "./PickerAnnouncement";
import { usePickerRoot } from "./PickerContext";
import { PickerSeparator } from "./PickerSection";
import { composeHandlers } from "@design-system/overlay/composeHandlers";
import {
    usePickerCellEditorKeyDown,
    usePickerCellEditorSeed,
} from "./usePickerCellEditorKeys";
import { usePickerFooterScrollPadding } from "./usePickerFooterScrollPadding";
import { usePickerLevelKeyboard } from "./usePickerLevelKeyboard";
import styles from "./Picker.module.scss";

export interface PickerSearchProps {
    placeholder?: string;
    /** Focus the input when it appears: on popup open (default `true`) or, inline, once on mount (default `false`). */
    autoFocus?: boolean;
    className?: string;
    /** Reaches the search `<input>`. */
    ref?: Ref<HTMLInputElement>;
}

/** Popup search bar, full-bleed and sticky at the popup top. */
export const PickerSearch = ({
    placeholder = "Search",
    autoFocus,
    className,
    ref,
}: PickerSearchProps) => {
    const root = usePickerRoot();
    const { kind, isInline, searchAutoFocusRef, setHasInlineSearch } = root;
    const isCombobox = kind === "combobox";
    const shouldAutoFocus = autoFocus ?? !isInline;
    const inputRef = useRef<HTMLInputElement>(null);
    const mergedInputRef = useMemo(
        () => mergeRefs([inputRef, ref]),
        [inputRef, ref],
    );
    // Level keys run on the input itself so Enter reaches a flyout row before
    // Base UI's own keydown would select it.
    const handleLevelKeyDown = usePickerLevelKeyboard();
    const handleCellEditorKeyDown = usePickerCellEditorKeyDown();
    usePickerCellEditorSeed();

    // The ref means "a search input is mounted": set only while a combobox
    // root renders the input, restored to the null sentinel on unmount.
    useLayoutEffect(() => {
        if (!isCombobox) {
            return;
        }
        searchAutoFocusRef.current = shouldAutoFocus;
        if (isInline) {
            setHasInlineSearch(true);
        }
        return () => {
            searchAutoFocusRef.current = null;
            if (isInline) {
                setHasInlineSearch(false);
            }
        };
    }, [
        isCombobox,
        isInline,
        searchAutoFocusRef,
        setHasInlineSearch,
        shouldAutoFocus,
    ]);

    // No popup opens an inline root, so its search focuses itself; without
    // scrolling, since the panel around it may still be positioning.
    useEffect(() => {
        if (isInline && shouldAutoFocus) {
            inputRef.current?.focus({ preventScroll: true });
        }
    }, [isInline, shouldAutoFocus]);

    if (!isCombobox) {
        return null;
    }

    return (
        <div
            className={classNames(styles.search, styles.searchField, className)}
            data-disabled={root.isDisabled ? "" : undefined}
        >
            <ComboboxBase.Input
                ref={mergedInputRef}
                className={styles.searchInput}
                placeholder={placeholder}
                onKeyDown={composeHandlers(
                    handleLevelKeyDown,
                    handleCellEditorKeyDown,
                )}
            />
        </div>
    );
};

export interface PickerFooterProps {
    children: ReactNode;
    className?: string;
    /** The full-bleed divider above the footer; drop it when nothing renders above (default `true`). */
    separator?: boolean;
}

/** Pinned section under a full-bleed separator; usually holds one action item. */
export const PickerFooter = ({
    children,
    className,
    separator = true,
}: PickerFooterProps) => {
    const root = usePickerRoot();
    const footerRef = useRef<HTMLDivElement>(null);
    usePickerFooterScrollPadding(footerRef);

    return (
        <div
            ref={footerRef}
            className={classNames(
                styles.footer,
                root.classNames.footer,
                className,
            )}
            data-picker-part="footer"
        >
            {separator && <PickerSeparator />}
            {children}
        </div>
    );
};

export interface PickerStatusRowProps {
    children?: ReactNode;
    className?: string;
}

/**
 * Empty-state row; the root renders it when no row survives filtering and
 * `emptyBehavior` is `"show"`. Announced politely by the enclosing surface.
 */
export const PickerEmpty = ({
    children = "No results",
    className,
}: PickerStatusRowProps) => {
    usePickerAnnouncement(children, "polite");

    return (
        <div
            className={classNames(styles.statusRow, className)}
            data-picker-part="status"
        >
            {children}
        </div>
    );
};

/** Loading row: 16px spinner and tertiary copy. */
export const PickerLoading = ({
    children = "Loading",
    className,
}: PickerStatusRowProps) => (
    <div
        className={classNames(styles.statusRow, className)}
        data-picker-part="status"
    >
        <Loader inline className={styles.statusLoader} />
        <span>{children}</span>
    </div>
);

/** Error row in the error foreground. Announced assertively by the enclosing surface. */
export const PickerError = ({
    children = "Something went wrong",
    className,
}: PickerStatusRowProps) => {
    usePickerAnnouncement(children, "assertive");

    return (
        <div
            className={classNames(styles.statusRow, styles.errorRow, className)}
            data-picker-part="status"
        >
            {children}
        </div>
    );
};
